// src/context/NotificationContext.js
import React, { createContext, useContext, useCallback, useRef } from "react";

const NotificationContext = createContext();

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const audioRef = useRef(null);

  const playNotificationSound = useCallback((soundUrl = null) => {
    try {
      if (soundUrl) {
        if (!audioRef.current) {
          audioRef.current = new Audio(soundUrl);
          audioRef.current.volume = 0.5;
        }
        audioRef.current.play().catch((error) => {
          // Audio play failed, ignore silently
        });
      } else {
        // Simple beep using Web Audio API
        if ("AudioContext" in window || "webkitAudioContext" in window) {
          const AudioContext = window.AudioContext || window.webkitAudioContext;
          const audioContext = new AudioContext();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
          gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(
            0.01,
            audioContext.currentTime + 0.1
          );

          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.1);
        }
      }
    } catch (error) {
      // Audio play failed, ignore silently
    }
  }, []);

  const showBrowserNotification = useCallback((title, options = {}) => {
    if (!("Notification" in window) || Notification.permission !== "granted") {
      return;
    }

    try {
      const notification = new Notification(title, {
        icon: "/favicon.ico",
        badge: "/favicon.ico",
        tag: "notification-update",
        renotify: false,
        ...options,
      });

      // Auto-close notification after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    } catch (error) {
      // Browser notification failed, ignore silently
    }
  }, []);

  const showEventBridgeNotification = useCallback(
    (eventBridgeMessage) => {
      if (!eventBridgeMessage?.data) return;

      const { data } = eventBridgeMessage;

      // Handle thread notifications
      if (data.type === "message.liked" || data.type === "message.replied") {
        const threadText = getThreadNotificationText(data);
        return showBrowserNotification(threadText.title, {
          body: threadText.message,
          icon: data.metadata?.actorPicture || "/favicon.ico",
          tag: data._id,
          data: eventBridgeMessage,
        });
      }

      // Handle regular notifications
      const title = getNotificationTitle(data.type);
      const body = `${data.metadata?.actorName} ${getNotificationMessage(
        data.type
      )}`;

      return showBrowserNotification(title, {
        body,
        icon: data.metadata?.actorPicture || "/favicon.ico",
        tag: data._id,
        data: eventBridgeMessage,
      });
    },
    [showBrowserNotification]
  );

  const updateDocumentTitle = useCallback((unreadCount) => {
    const baseTitle = document.title.replace(/^\(\d+\)\s*/, "");
    document.title =
      unreadCount > 0 ? `(${unreadCount}) ${baseTitle}` : baseTitle;
  }, []);

  const value = {
    playNotificationSound,
    showBrowserNotification,
    showEventBridgeNotification,
    updateDocumentTitle,
  };

  // Helper functions for EventBridge notifications
  const getNotificationTitle = (type) => {
    const titles = {
      follow: "New Follower",
      unfollow: "Unfollowed",
      "message.liked": "Message Liked",
      "message.replied": "New Reply",
    };
    return titles[type] || "New Notification";
  };

  const getNotificationMessage = (type) => {
    const messages = {
      follow: "started following you",
      unfollow: "unfollowed you",
      "message.liked": "liked your message",
      "message.replied": "replied to your message",
    };
    return messages[type] || "sent you a notification";
  };

  const getThreadNotificationText = (data) => {
    const actorName = data.metadata?.actorName || "Someone";
    const threadTitle = data.metadata?.threadTitle || "a discussion";

    switch (data.type) {
      case "message.liked":
        return {
          title: "Message Liked",
          message: `${actorName} liked your message in "${threadTitle}"`,
        };
      case "message.replied":
        return {
          title: "New Reply",
          message: `${actorName} replied to your message in "${threadTitle}"`,
        };
      default:
        return {
          title: "Thread Notification",
          message: `${actorName} interacted with your content`,
        };
    }
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
